import { unwrap, type components } from "@sagot/api-client";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { engageKeys } from "../shared/engage-keys";
import { OPEN_PAGE_LIMIT, RESOLVED_PAGE_LIMIT, type GapIgnoreReason, type GapSort } from "./gap-model";

export interface GapFilters {
  agentId: string | null;
  q: string;
  sort: GapSort;
}

type ResolveBody = components["schemas"]["GapResolveIn"];
type BulkBody = components["schemas"]["GapBulkIn"];

const query = (filters: GapFilters) => ({
  agent_id: filters.agentId,
  q: filters.q.trim() || null,
  sort: filters.sort,
});

export function useGapStats() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: [...engageKeys.gaps(wsId), "stats"], queryFn: () => unwrap(api.GET("/v1/gaps/stats")) });
}

/** All open clusters for the filters (tabs split them client-side by reason). */
export function useOpenGaps(filters: GapFilters) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: [...engageKeys.gaps(wsId), "open", filters],
    queryFn: async () =>
      (await unwrap(api.GET("/v1/gaps", { params: { query: { ...query(filters), status: "open", limit: OPEN_PAGE_LIMIT } } }))).items,
  });
}

export function useResolvedGaps(filters: GapFilters, enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: [...engageKeys.gaps(wsId), "resolved", filters],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      unwrap(api.GET("/v1/gaps", { params: { query: { ...query(filters), status: "resolved", limit: RESOLVED_PAGE_LIMIT, cursor: pageParam } } })),
    getNextPageParam: (page) => page.next_cursor,
  });
}

export function useGapDetail(gapId: string | null) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: [...engageKeys.gaps(wsId), "detail", gapId],
    enabled: gapId !== null,
    queryFn: () => unwrap(api.GET("/v1/gaps/{gap_id}", { params: { path: { gap_id: gapId ?? "" } } })),
  });
}

function useInvalidateGaps() {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return () => queryClient.invalidateQueries({ queryKey: engageKeys.gaps(wsId) });
}

export function useAssignGap() {
  const api = useApi();
  const invalidate = useInvalidateGaps();
  return useMutation({
    mutationFn: ({ gapId, userId }: { gapId: string; userId: string | null }) =>
      unwrap(
        api.PATCH("/v1/gaps/{gap_id}", {
          params: { path: { gap_id: gapId } },
          body: userId ? { assignee_user_id: userId, clear_assignee: false } : { clear_assignee: true },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useIgnoreGap() {
  const api = useApi();
  const invalidate = useInvalidateGaps();
  return useMutation({
    mutationFn: ({ gapId, reason }: { gapId: string; reason: GapIgnoreReason }) =>
      unwrap(api.PATCH("/v1/gaps/{gap_id}", { params: { path: { gap_id: gapId } }, body: { status: "ignored", ignore_reason: reason, clear_assignee: false } })),
    onSuccess: invalidate,
  });
}

export function useBulkGaps() {
  const api = useApi();
  const invalidate = useInvalidateGaps();
  return useMutation({
    mutationFn: (body: BulkBody) => unwrap(api.POST("/v1/gaps/bulk", { body })),
    onSuccess: invalidate,
  });
}

export function useSuggestAnswer() {
  const api = useApi();
  return useMutation({
    mutationFn: (gapId: string) => unwrap(api.POST("/v1/gaps/{gap_id}/suggest", { params: { path: { gap_id: gapId } } })),
  });
}

export function useResolveGap() {
  const api = useApi();
  const invalidate = useInvalidateGaps();
  return useMutation({
    mutationFn: ({ gapId, body }: { gapId: string; body: ResolveBody }) =>
      unwrap(api.POST("/v1/gaps/{gap_id}/resolve", { params: { path: { gap_id: gapId } }, body })),
    onSuccess: invalidate,
  });
}

/** `GET /v1/gaps/export` (CSV of the current agent's open gaps). */
export function useExportGaps() {
  const api = useApi();
  return useMutation({
    mutationFn: (agentId: string | null) =>
      unwrap(api.GET("/v1/gaps/export", { params: { query: { status: "open", agent_id: agentId } }, parseAs: "blob" })),
  });
}
